import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { DisclaimerArchiveButton } from "@/components/DisclaimerArchive";
import { PageHeader } from "@/components/StatCard";
import { CommissionProgress, CommissionBadge } from "@/components/CommissionProgress";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, ShieldCheck, Ban, Loader2 } from "lucide-react";
import { UserPricingButton } from "@/components/UserPricingButton";

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
const empty = { email: "", password: "", first_name: "", last_name: "", phone: "", nmls_number: "", role: "mlo", state_licenses: "" };

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/users").then((r) => setUsers([...r.data].sort((a, b) => (b.account_status === "pending") - (a.account_status === "pending"))));
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/admin/users", { ...form, origin_url: window.location.origin, state_licenses: form.state_licenses.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success("Account created — welcome email sent"); setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const toggleStatus = async (u) => {
    const next = u.account_status === "active" ? "suspended" : "active";
    await api.patch(`/admin/users/${u.id}`, { account_status: next }); toast.success(`Account ${next}`); load();
  };
  const approve = async (u) => {
    await api.patch(`/admin/users/${u.id}`, { account_status: "active" }); toast.success("Account approved — welcome email sent"); load();
  };
  const decline = async (u) => {
    try { await api.post(`/admin/users/${u.id}/decline`); toast.success("Request declined"); load(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="MLO Accounts" subtitle="Create and manage loan originator accounts.">
        {users.some((u) => u.account_status === "pending") && (
          <span className="rounded-full bg-amber-100 px-3 py-1.5 text-sm font-semibold text-amber-800" data-testid="pending-requests-count">
            {users.filter((u) => u.account_status === "pending").length} pending request(s)
          </span>
        )}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="gold-cta border-0 font-bold" data-testid="add-user-btn"><UserPlus className="mr-2 h-4 w-4" /> Add Account</Button>
          </DialogTrigger>
          <DialogContent data-testid="user-dialog">
            <DialogHeader><DialogTitle>New Account</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>First name</Label><Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="new-first" /></div>
              <div><Label>Last name</Label><Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="new-last" /></div>
              <div className="col-span-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="new-email" /></div>
              <div className="col-span-2"><Label>Temporary password (optional)</Label><Input value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="Leave blank — they'll set it from the welcome email" data-testid="new-password" /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="new-phone" /></div>
              <div><Label>NMLS #</Label><Input value={form.nmls_number} onChange={(e) => set("nmls_number", e.target.value)} data-testid="new-nmls" /></div>
              <div className="col-span-2"><Label>State licenses (comma-separated)</Label><Input value={form.state_licenses} onChange={(e) => set("state_licenses", e.target.value)} data-testid="new-states" /></div>
              <div className="col-span-2"><Label>Role</Label>
                <select value={form.role} onChange={(e) => set("role", e.target.value)} className="mt-1 h-10 w-full rounded-md border border-slate-200 px-3 text-sm" data-testid="new-role">
                  <option value="mlo">Mortgage Loan Originator</option><option value="admin">Administrator</option>
                </select>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} disabled={busy || !form.email} data-testid="create-user-btn" className="bg-slate-900 hover:bg-slate-800">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Account"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-100 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Role</th><th className="hidden px-4 py-3 md:table-cell">Runs</th><th className="hidden px-4 py-3 md:table-cell">Total spent</th><th className="px-4 py-3">Weekly spending & commission</th><th className="hidden px-4 py-3 lg:table-cell">Prospects</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3"><div className="font-semibold text-slate-900">{u.first_name} {u.last_name}</div><div className="text-xs text-slate-500">{u.email} · NMLS {u.nmls_number || "—"}</div></td>
                <td className="px-4 py-3"><Badge variant="outline" className="capitalize">{u.role === "admin" ? "Administrator" : "MLO"}</Badge></td>
                <td className="hidden px-4 py-3 md:table-cell">{u.runs_purchased}</td>
                <td className="hidden px-4 py-3 md:table-cell" data-testid={`total-spent-${u.id}`}>{money(u.total_spent)}<div className="text-xs text-slate-500" data-testid={`weekly-avg-${u.id}`}>{money(u.spending?.weekly_avg)} / week avg</div></td>
                <td className="min-w-[220px] px-4 py-3" data-testid={`commission-cell-${u.id}`}>
                  {u.role !== "admin" && u.spending && (<>
                    <div className="mb-2"><CommissionBadge s={u.spending} testId={`commission-badge-${u.id}`} /></div>
                    <CommissionProgress s={u.spending} compact />
                  </>)}
                </td>
                <td className="hidden px-4 py-3 lg:table-cell">{u.total_prospects} ({u.total_interested} int.)</td>
                <td className="px-4 py-3"><Badge className={`${u.account_status === "active" ? "bg-emerald-100 text-emerald-800" : u.account_status === "pending" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800"} border-0 capitalize`}>{u.account_status}</Badge></td>
                <td className="px-4 py-3">
                  {u.role !== "admin" && <UserPricingButton u={u} onSaved={load} />}
                  {u.role !== "admin" && u.account_status === "active" && (
                    <Button size="sm" variant="ghost" className={u.marketplace_access ? "text-indigo-700" : "text-slate-600"} data-testid={`marketplace-access-${u.id}`}
                      onClick={async () => { try { await api.patch(`/admin/users/${u.id}`, { marketplace_access: !u.marketplace_access }); toast.success(u.marketplace_access ? "Marketplace access removed" : "Marketplace access granted"); load(); } catch (e) { toast.error(apiError(e)); } }}>
                      {u.marketplace_access ? "Marketplace: on" : "Marketplace: off"}
                    </Button>
                  )}
                  {u.role !== "admin" && u.account_status === "active" && (
                    <Button size="sm" variant="ghost" className={u.deity_ai_sourced ? "text-amber-700" : "text-slate-600"} data-testid={`deity-ai-sourced-${u.id}`}
                      title="Let AI Autopilot Mode call this MLO's AI-sourced (lead engine) leads"
                      onClick={async () => { try { await api.patch(`/admin/users/${u.id}`, { deity_ai_sourced: !u.deity_ai_sourced }); toast.success(u.deity_ai_sourced ? "AI Autopilot Mode will skip AI-sourced leads" : "AI Autopilot Mode can now call AI-sourced leads"); load(); } catch (e) { toast.error(apiError(e)); } }}>
                      {u.deity_ai_sourced ? "Autopilot AI leads: on" : "Autopilot AI leads: off"}
                    </Button>
                  )}
                  {u.role !== "admin" && u.account_status === "active" && [["live_transfer_free", "Free transfers", "Live transfers are no longer free", "Live transfers are now free for this MLO"]].map(([k, label, off, on]) => (
                    <Button key={k} size="sm" variant="ghost" className={u[k] ? "text-indigo-700" : "text-slate-600"} data-testid={`${k.replace(/_/g, "-")}-${u.id}`}
                      onClick={async () => { try { await api.patch(`/admin/users/${u.id}`, { [k]: !u[k] }); toast.success(u[k] ? off : on); load(); } catch (e) { toast.error(apiError(e)); } }}>
                      {label}: {u[k] ? "on" : "off"}
                    </Button>
                  ))}
                  {u.role !== "admin" && <DisclaimerArchiveButton u={u} />}
                  {u.role !== "admin" && u.account_status === "pending" && (
                    <Button size="sm" variant="ghost" className="text-emerald-700" onClick={() => approve(u)} data-testid={`approve-${u.id}`}>
                      <ShieldCheck className="mr-1 h-3.5 w-3.5" /> Approve
                    </Button>
                  )}
                  {u.role !== "admin" && u.account_status === "pending" && (
                    <Button size="sm" variant="ghost" className="text-red-700" onClick={() => decline(u)} data-testid={`decline-${u.id}`}>
                      <Ban className="mr-1 h-3.5 w-3.5" /> Decline
                    </Button>
                  )}
                  {u.role !== "admin" && !["pending", "declined"].includes(u.account_status) && (
                    <Button size="sm" variant="ghost" onClick={() => toggleStatus(u)} data-testid={`toggle-status-${u.id}`}>
                      {u.account_status === "active" ? <><Ban className="mr-1 h-3.5 w-3.5" /> Suspend</> : <><ShieldCheck className="mr-1 h-3.5 w-3.5" /> Activate</>}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
